import { useMemo, useState } from 'react';
import { emailSchema, getReplyEmailError } from '@/lib/validationSchemas';

/**
 * Small helper that gives every email input the same "aware" behavior:
 * - Validates against the shared Zod `emailSchema` (RFC-ish, trimmed, ≤255).
 * - Shows an inline error only after the field has been touched (blurred) or
 *   after a submit attempt, so we never yell at users mid-typing.
 * - Exposes `isValid` so callers can disable Submit until the value is real.
 *
 * Usage:
 *   const email = useEmailField();
 *   <Input type="email" value={email.value} onChange={e => email.setValue(e.target.value)}
 *          onBlur={email.onBlur} aria-invalid={email.showError} />
 *   {email.showError && <p className="text-destructive">{email.error}</p>}
 *   <Button disabled={!email.isValid} onClick={() => { if (!email.validate()) return; ... }}>
 */
export function useEmailField(initial = '', options?: { requireReplyable?: boolean }) {
	const [value, setValue] = useState(initial);
	const [touched, setTouched] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const parsed = useMemo(() => emailSchema.safeParse(value), [value]);
	const replyError = useMemo(
		() => (options?.requireReplyable ? getReplyEmailError(value) : null),
		[options?.requireReplyable, value]
	);
	const isValid = parsed.success && !replyError;
	const error = replyError ?? (parsed.success ? null : parsed.error.issues[0]?.message ?? 'Invalid email address');

	return {
		value,
		setValue: (v: string) => setValue(v),
		onBlur: () => setTouched(true),
		reset: () => {
			setValue('');
			setTouched(false);
			setSubmitted(false);
		},
		/** Call from submit handler; returns whether the value is valid. */
		validate: () => {
			setSubmitted(true);
			setTouched(true);
			return isValid;
		},
		isValid,
		error,
		/** True once the user has interacted OR tried to submit AND value is bad. */
		showError: (touched || submitted) && !isValid && value.length > 0,
		/** True after a bad submit attempt on empty field. */
		showEmptyError: submitted && value.trim().length === 0,
		/** Trimmed, lowercased value ready to send. */
		normalized: value.trim().toLowerCase(),
	};
}
